/**
 * documentBody — where a document's whole text lives once the site record
 * only carries the preview (document-intelligence plan P1.1 / P1.4).
 *
 *   - In the upload session the `SiteDocument` in memory carries
 *     `fullExtractedText`.
 *   - After a reload a source-backed row has a 1k `textContent` preview
 *     plus `extractedCharsKept` saying how much body exists; the body
 *     itself is in the per-document GCS JSON (`storagePath`, whose
 *     `textContent` / `fullExtractedText` hold the whole text) and in the
 *     chunk subcollection.
 *
 * `hydrateDocumentBodies` fills `fullExtractedText` from the GCS JSON for the
 * documents that need it, through an injectable loader: the browser default
 * uses the authenticated download proxy; the server bootstrap passes an admin
 * loader. Best-effort by contract — a document whose body cannot be loaded is
 * returned unchanged, so callers degrade to the preview instead of failing.
 */
import type { DocumentTopicSegment, SiteDocument } from '../types/siteRag';
import { hasSourceBackedDocumentBody } from './siteDocumentPersistence';

/** What a loader may return: the body alone, or the body with the content index (P4). */
export interface DocumentBodyPayload {
  body: string;
  topicSegments?: DocumentTopicSegment[];
  visualReadings?: SiteDocument['visualReadings'];
}

export type DocumentBodyLoader = (doc: SiteDocument) => Promise<string | DocumentBodyPayload | null>;

/** The text a consumer should read: whole body when present, else the preview. */
export function documentBodyText(doc: Pick<SiteDocument, 'fullExtractedText' | 'textContent'>): string {
  return doc.fullExtractedText ?? doc.textContent ?? '';
}

/** Page text is redundant on compact site records; reconstruct it from the
 * retained body/preview using unchanged offsets. Legacy inline text remains a
 * fallback when only a shorter preview is available. */
export function pageTextFromDocument(
  doc: { fullExtractedText?: string; textContent?: string },
  page: NonNullable<SiteDocument['pages']>[number],
): string {
  const body = doc.fullExtractedText ?? doc.textContent ?? '';
  return body.length >= page.charEnd
    ? body.slice(page.charStart, page.charEnd)
    : page.text || body.slice(page.charStart, page.charEnd);
}

/** GCS JSON path for a document; `storagePath` is stamped at upload (P1.2), legacy docs fall back to the caller's uid. */
export function documentJsonPath(doc: Pick<SiteDocument, 'id' | 'storagePath'>, fallbackUid: string): string {
  return doc.storagePath ?? `users/${fallbackUid}/documents/${doc.id}.json`;
}

/** Owner uid encoded in a `users/{uid}/documents/...` path, when present. */
export function ownerUidFromStoragePath(storagePath: string | undefined): string | undefined {
  const m = /^users\/([^/]+)\/documents\//.exec(storagePath ?? '');
  return m?.[1];
}

/**
 * True when more body exists than the object carries. Pre-P1.2 uploads never
 * captured more than the preview, so they are never "hydratable".
 */
export function documentNeedsBodyHydration(doc: SiteDocument): boolean {
  const have = doc.fullExtractedText?.length ?? doc.textContent?.length ?? 0;
  const kept = doc.extractedCharsKept;
  return typeof kept === 'number' && kept > have;
}

/** P4 — the site record keeps only the compact index; the segments live in the document JSON. */
export function documentNeedsTopicHydration(doc: SiteDocument): boolean {
  return ((doc.topics?.segmentCount ?? 0) > 0 && !doc.topicSegments)
    || ((doc.visualReadingCount ?? 0) > 0 && !doc.visualReadings);
}

const BODY_CACHE_MAX = 40;
const bodyCache = new Map<string, DocumentBodyPayload>();

function cacheKey(doc: SiteDocument): string {
  return `${doc.id}::${doc.storagePath ?? ''}::${doc.contentHash ?? ''}::${doc.extractedCharsKept ?? ''}::${doc.updatedAt ?? ''}::${doc.topics?.version ?? ''}`;
}

function remember(key: string, payload: DocumentBodyPayload): void {
  if (bodyCache.has(key)) bodyCache.delete(key);
  bodyCache.set(key, payload);
  while (bodyCache.size > BODY_CACHE_MAX) {
    const oldest = bodyCache.keys().next().value;
    if (oldest === undefined) break;
    bodyCache.delete(oldest);
  }
}

/** Test hook. */
export function clearDocumentBodyCache(): void {
  bodyCache.clear();
}

/** Browser loader: the authenticated storage download proxy, same path DocViewer uses. */
export const browserDocumentBodyLoader: DocumentBodyLoader = async (doc) => {
  const { auth } = await import('../firebase');
  const uid = auth.currentUser?.uid;
  if (!uid) return null;
  const { authedFetch } = await import('./authedFetch');
  const path = documentJsonPath(doc, uid);
  const res = await authedFetch(`/api/storage/download-file?path=${encodeURIComponent(path)}`);
  if (!res.ok) return null;
  const json = (await res.json()) as Partial<SiteDocument>;
  return checkedDocumentBodyPayload(doc, json);
};

/** Preserve the same captured body identity at both authenticated read seams. */
function checkedDocumentBodyPayload(doc: SiteDocument, json: Partial<SiteDocument>): DocumentBodyPayload | null {
  const body = json.fullExtractedText ?? json.textContent;
  if (typeof body !== 'string' || body.length === 0) return null;
  if (hasSourceBackedDocumentBody(doc) && (json.id !== doc.id || json.contentHash !== doc.contentHash
    || body.length !== doc.extractedCharsKept)) return null;
  return {
    body,
    ...(Array.isArray(json.topicSegments) && json.topicSegments.length > 0 ? { topicSegments: json.topicSegments } : {}),
    ...(Array.isArray(json.visualReadings) && json.visualReadings.length > 0 ? { visualReadings: json.visualReadings } : {}),
  };
}

/** A canonical site context is a lookup hint; the server independently admits
 * only its current enabled owner/platform-admin and checks the stored index.
 * Injected loaders deliberately do not reuse the browser's shared body cache. */
export function createSiteDocumentBodyLoader(context: { siteId: string; ownerUid: string }): DocumentBodyLoader {
  return async doc => {
    const { auth } = await import('../firebase');
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    // Own-document reads retain the existing owner route, including legacy rows.
    const path = documentJsonPath(doc, uid);
    if (path === `users/${uid}/documents/${doc.id}.json`) return browserDocumentBodyLoader(doc);
    if (!/^[A-Za-z0-9_-]+$/.test(context.ownerUid) || !/^[A-Za-z0-9_-]+$/.test(context.siteId)
      || !hasSourceBackedDocumentBody(doc)
      || path !== `users/${context.ownerUid}/documents/${doc.id}.json`) return null;
    const { authedFetch } = await import('./authedFetch');
    const res = await authedFetch('/api/site-document-body', { method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerUid: context.ownerUid, siteId: context.siteId, documentId: doc.id }) });
    if (!res.ok) return null;
    return checkedDocumentBodyPayload(doc, await res.json() as Partial<SiteDocument>);
  };
}

/** Apply a loaded payload to a document: a longer body replaces the preview; missing segments are restored. */
function applyPayload(doc: SiteDocument, payload: DocumentBodyPayload): SiteDocument {
  const have = doc.fullExtractedText?.length ?? doc.textContent?.length ?? 0;
  const next: SiteDocument = { ...doc };
  let changed = false;
  if (payload.body.length > have) {
    next.fullExtractedText = payload.body;
    changed = true;
  }
  if (!doc.topicSegments && payload.topicSegments && payload.topicSegments.length > 0) {
    next.topicSegments = payload.topicSegments;
    changed = true;
  }
  if (!doc.visualReadings && payload.visualReadings?.length) {
    next.visualReadings = payload.visualReadings;
    next.visualReadingCount = payload.visualReadings.length;
    changed = true;
  }
  return changed ? next : doc;
}

export interface HydrateDocumentBodiesOptions {
  loader?: DocumentBodyLoader;
  /** Parallel loads; bodies are up to 2 MB each. */
  concurrency?: number;
  /** Only hydrate documents this predicate accepts (default: all that need it). */
  filter?: (doc: SiteDocument) => boolean;
}

/**
 * Return a copy of `docs` where every document that needs its body and whose
 * body could be loaded carries `fullExtractedText`. Never throws.
 */
export async function hydrateDocumentBodies(
  docs: readonly SiteDocument[],
  opts: HydrateDocumentBodiesOptions = {},
): Promise<SiteDocument[]> {
  const loader = opts.loader ?? browserDocumentBodyLoader;
  // Injected loaders carry their own authority (for example a site owner).
  // A prior browser or different owner read cannot authorize their next read.
  const useSharedCache = opts.loader === undefined;
  const concurrency = Math.max(1, opts.concurrency ?? 3);
  const out = [...docs];
  const targets = out
    .map((doc, index) => ({ doc, index }))
    .filter(({ doc }) => (documentNeedsBodyHydration(doc) || documentNeedsTopicHydration(doc)) && (opts.filter?.(doc) ?? true));
  let cursor = 0;
  const worker = async () => {
    while (cursor < targets.length) {
      const { doc, index } = targets[cursor++]!;
      const key = cacheKey(doc);
      const cached = useSharedCache ? bodyCache.get(key) : undefined;
      if (cached !== undefined) {
        out[index] = applyPayload(doc, cached);
        continue;
      }
      try {
        const loaded = await loader(doc);
        const payload = typeof loaded === 'string' ? { body: loaded } : loaded;
        if (payload && payload.body.length > 0) {
          out[index] = applyPayload(doc, payload);
          // A short/partial response must be retried, not cached forever as
          // the document's complete source after evidence readers refuse it.
          if (useSharedCache && !documentNeedsBodyHydration(out[index]!) && !documentNeedsTopicHydration(out[index]!)) remember(key, payload);
        }
      } catch (e) {
        console.warn(`[documentBody] body load failed for ${doc.id} (${doc.fileName}); using the preview:`, (e as Error)?.message);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, targets.length) }, worker));
  return out;
}

/** Evidence consumers must never treat a failed full-body read as a complete
 * 1k preview. UI callers may catch this and display the unavailable source. */
export async function hydrateDocumentBodiesForEvidence(
  docs: readonly SiteDocument[], opts: HydrateDocumentBodiesOptions = {},
): Promise<SiteDocument[]> {
  const hydrated = await hydrateDocumentBodies(docs, opts);
  const incomplete = hydrated.filter(documentNeedsBodyHydration);
  if (incomplete.length) throw new Error(`Full document text is unavailable: ${incomplete.map(doc => doc.fileName).join(', ')}. Retry the source read before using this evidence.`);
  return hydrated;
}

/** Page number containing a char offset into the body, from `pages[]` offsets; undefined when unknown. */
export function pageForCharOffset(
  pages: SiteDocument['pages'] | undefined,
  offset: number | undefined,
): number | undefined {
  if (!pages?.length || typeof offset !== 'number' || offset < 0) return undefined;
  for (const p of pages) {
    if (offset >= p.charStart && offset < Math.max(p.charEnd, p.charStart + 1)) return p.page;
  }
  return undefined;
}
